import React, { useEffect } from 'react';
import { Link } from 'react-router-dom';
import { BookOpen, Users, Trash2, PlusCircle } from 'lucide-react';
import { useAppDispatch, useAppSelector } from '../../hooks';
import { fetchBooks, fetchDeletedBooks } from '../../store/books/booksSlice';
import { fetchConsumers, fetchAdmins } from '../../store/users/usersSlice';
import { LoadingSpinner } from '../../components/common';

export const DashboardPage: React.FC = () => {
  const dispatch = useAppDispatch();
  const { total: totalBooks, deletedBooks, isLoading: booksLoading } = useAppSelector(
    (state) => state.books
  );
  const { consumers, admins, isLoading: usersLoading } = useAppSelector(
    (state) => state.users
  );

  useEffect(() => {
    dispatch(fetchBooks({ limit: 1 }));
    dispatch(fetchDeletedBooks());
    dispatch(fetchConsumers());
    dispatch(fetchAdmins());
  }, [dispatch]);

  const isLoading = booksLoading || usersLoading;

  const stats = [
    {
      label: 'Total Books',
      value: totalBooks,
      icon: BookOpen,
      bgColor: 'bg-blue-50',
      textColor: 'text-blue-600',
      link: '/admin/books',
    },
    {
      label: 'Recyle Bin',
      value: deletedBooks.length,
      icon: Trash2,
      bgColor: 'bg-red-50',
      textColor: 'text-red-600',
      link: '/admin/deleted-books',
    },
    {
      label: 'Members',
      value: consumers.length,
      icon: Users,
      bgColor: 'bg-green-50',
      textColor: 'text-green-600',
      link: '/admin/users',
    },
    {
      label: 'Admins',
      value: admins.length,
      icon: Users,
      bgColor: 'bg-amber-50',
      textColor: 'text-amber-600',
      link: '/admin/users',
    },
  ];

  if (isLoading) {
    return (
      <div className="flex justify-center py-12">
        <LoadingSpinner size="lg" />
      </div>
    );
  }

  return (
    <div>
      <div className="mb-4 sm:mb-6 lg:mb-8">
        <h1 className="text-xl sm:text-2xl lg:text-3xl font-bold text-slate-900 mb-1 sm:mb-2">Dashboard</h1>
        <p className="text-sm sm:text-base text-slate-500">Welcome to the admin panel</p>
      </div>

      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 sm:gap-4 lg:gap-6 mb-4 sm:mb-6 lg:mb-8">
        {stats.map((stat) => (
          <Link
            key={stat.label}
            to={stat.link}
            className="bg-white rounded-lg sm:rounded-xl shadow-sm p-3 sm:p-4 lg:p-6 hover:shadow-md transition-shadow flex flex-col sm:flex-row items-center gap-2 sm:gap-4"
          >
            <div className={`w-10 h-10 sm:w-12 sm:h-12 rounded-lg ${stat.bgColor} flex items-center justify-center flex-shrink-0`}>
              <stat.icon size={20} className={`${stat.textColor} sm:w-6 sm:h-6`} />
            </div>
            <div className="text-center sm:text-left">
              <span className="text-xl sm:text-2xl font-bold text-slate-900 block">{stat.value}</span>
              <span className="text-xs sm:text-sm text-slate-500">{stat.label}</span>
            </div>
          </Link>
        ))}
      </div>

      <div className="bg-white rounded-lg sm:rounded-xl shadow-sm p-4 sm:p-6">
        <h2 className="text-base sm:text-lg font-semibold text-slate-900 mb-3 sm:mb-4">Quick Actions</h2>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 sm:gap-4">
          <Link
            to="/admin/books/add"
            className="flex items-center gap-2 sm:gap-3 p-3 sm:p-4 border border-slate-200 rounded-lg hover:bg-slate-50 transition-colors"
          >
            <PlusCircle size={20} className="text-blue-600 sm:w-6 sm:h-6" />
            <span className="text-sm sm:text-base font-medium text-slate-700">Add New Book</span>
          </Link>
          <Link
            to="/admin/users/add-admin"
            className="flex items-center gap-2 sm:gap-3 p-3 sm:p-4 border border-slate-200 rounded-lg hover:bg-slate-50 transition-colors"
          >
            <Users size={20} className="text-blue-600 sm:w-6 sm:h-6" />
            <span className="text-sm sm:text-base font-medium text-slate-700">Add New Admin</span>
          </Link>
        </div>
      </div>
    </div>
  );
};
